import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Edit, Trash2, Save, Eye, X, ChevronDown, Search,
  CheckCircle2, Bold, Italic, Heading1, Heading2, List, Link,
  Quote, ImageIcon
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API, axios, showToast } from './adminHelpers';

const RichTextToolbar = ({ onInsert }) => {
  const tools = [
    { icon: Bold, label: 'Bold', action: () => onInsert('<strong>', '</strong>') },
    { icon: Italic, label: 'Italic', action: () => onInsert('<em>', '</em>') },
    { icon: Heading1, label: 'Heading 1', action: () => onInsert('<h2>', '</h2>') },
    { icon: Heading2, label: 'Heading 2', action: () => onInsert('<h3>', '</h3>') },
    { icon: List, label: 'List', action: () => onInsert('<ul>\n  <li>', '</li>\n</ul>') },
    { icon: Quote, label: 'Quote', action: () => onInsert('<blockquote>', '</blockquote>') },
    { icon: Link, label: 'Link', action: () => onInsert('<a href="URL">', '</a>') },
    { icon: ImageIcon, label: 'Image', action: () => onInsert('<img src="', '" alt="description" class="rounded-lg w-full" />') },
  ];

  return (
    <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border-b rounded-t-lg">
      {tools.map((tool, idx) => (
        <button key={idx} type="button" onClick={tool.action} className="p-2 hover:bg-gray-200 rounded transition-colors" title={tool.label}>
          <tool.icon className="w-4 h-4 text-gray-600" />
        </button>
      ))}
    </div>
  );
};

const PostEditor = ({ post, onSave, onCancel }) => {
  const contentRef = useRef(null);
  const [formData, setFormData] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    featured_image: post?.featured_image || '',
    category: post?.category || 'uncategorized',
    status: post?.status || 'draft',
    meta_title: post?.meta_title || '',
    meta_description: post?.meta_description || '',
  });
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title),
      meta_title: prev.meta_title || title
    }));
  };

  const insertAtCursor = (before, after) => {
    const textarea = contentRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = formData.content.substring(start, end);
    const newContent = formData.content.substring(0, start) + before + selected + after + formData.content.substring(end);
    setFormData(prev => ({ ...prev, content: newContent }));
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selected.length);
    }, 0);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) { showToast('Title is required', 'error'); return; }
    if (!formData.slug.trim()) { showToast('Slug is required', 'error'); return; }
    setSaving(true);
    try {
      await onSave(formData);
      showToast(post ? 'Post updated!' : 'Post created!');
    } catch {
      showToast('Failed to save post', 'error');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (formData.excerpt && !formData.meta_description) {
      setFormData(prev => ({ ...prev, meta_description: formData.excerpt.substring(0, 160) }));
    }
  }, [formData.excerpt, formData.meta_description]);

  return (
    <div className="space-y-6" data-testid="post-editor">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="post-editor-back">
            <ChevronDown className="w-5 h-5 rotate-90" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{post ? 'Edit Post' : 'New Post'}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPreviewMode(!previewMode)} data-testid="post-preview-toggle">
            <Eye className="w-4 h-4 mr-2" /> {previewMode ? 'Edit' : 'Preview'}
          </Button>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary-hover" data-testid="post-save-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : post ? 'Update' : 'Publish'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Post Title</Label>
                <Input value={formData.title} onChange={handleTitleChange} placeholder="Enter an engaging title..." className="mt-2 text-xl font-semibold h-12" data-testid="post-title-input" />
              </div>
              <div>
                <Label className="text-sm text-gray-500">Permalink</Label>
                <div className="flex items-center mt-1 text-sm">
                  <span className="text-gray-400">/blog/</span>
                  <Input value={formData.slug} onChange={(e) => setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }))} placeholder="post-url" className="border-0 border-b rounded-none px-1 focus:ring-0" data-testid="post-slug-input" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <Label className="text-base font-semibold">Excerpt</Label>
            <p className="text-sm text-gray-500 mb-2">A brief summary shown in blog listings</p>
            <Textarea value={formData.excerpt} onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))} placeholder="Write a compelling summary..." rows={3} className="mt-1" data-testid="post-excerpt-input" />
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
              <Label className="text-base font-semibold">Content</Label>
              <span className="text-xs text-gray-400">{formData.content.length} characters</span>
            </div>
            {previewMode ? (
              <div className="p-6">
                <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: formData.content || '<p class="text-gray-400">No content yet...</p>' }} />
              </div>
            ) : (
              <>
                <RichTextToolbar onInsert={insertAtCursor} />
                <Textarea
                  ref={contentRef}
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Start writing your post content... You can use HTML tags."
                  rows={20}
                  className="border-0 rounded-none focus:ring-0 font-mono text-sm resize-y"
                  data-testid="post-content-input"
                />
              </>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Publish</h3>
            <div className="space-y-4">
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="mt-1" data-testid="post-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-yellow-500" /> Draft</span></SelectItem>
                    <SelectItem value="published"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500" /> Published</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uncategorized">Uncategorized</SelectItem>
                    <SelectItem value="news">News</SelectItem>
                    <SelectItem value="tips">Tips & Advice</SelectItem>
                    <SelectItem value="updates">Updates</SelectItem>
                    <SelectItem value="guides">Travel Guides</SelectItem>
                    <SelectItem value="promotions">Promotions</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Featured Image</h3>
            <Input value={formData.featured_image} onChange={(e) => setFormData(prev => ({ ...prev, featured_image: e.target.value }))} placeholder="Paste image URL..." className="mb-2" data-testid="post-featured-image" />
            {formData.featured_image ? (
              <div className="relative group">
                <img src={formData.featured_image} alt="Featured" className="rounded-lg w-full h-40 object-cover" />
                <button onClick={() => setFormData(prev => ({ ...prev, featured_image: '' }))} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-400">
                <ImageIcon className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Paste an image URL above</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Search className="w-4 h-4" /> SEO Settings</h3>
            <div className="space-y-4">
              <div>
                <Label>Meta Title</Label>
                <Input value={formData.meta_title} onChange={(e) => setFormData(prev => ({ ...prev, meta_title: e.target.value }))} placeholder="SEO title (60 chars max)" className="mt-1" />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-400">Appears in search results</span>
                  <span className={`text-xs ${formData.meta_title.length > 60 ? 'text-red-500' : 'text-gray-400'}`}>{formData.meta_title.length}/60</span>
                </div>
              </div>
              <div>
                <Label>Meta Description</Label>
                <Textarea value={formData.meta_description} onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))} placeholder="SEO description (160 chars max)" rows={3} className="mt-1" />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-400">Appears below title in search</span>
                  <span className={`text-xs ${formData.meta_description.length > 160 ? 'text-red-500' : 'text-gray-400'}`}>{formData.meta_description.length}/160</span>
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-400 mb-2">Search Preview</p>
              <div className="text-blue-600 text-sm font-medium truncate">{formData.meta_title || formData.title || 'Post Title'}</div>
              <div className="text-green-700 text-xs truncate">13cabsbook.com.au/blog/{formData.slug || 'post-url'}</div>
              <div className="text-gray-600 text-xs line-clamp-2 mt-1">{formData.meta_description || formData.excerpt || 'Add a meta description...'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PostsManager = ({ authHeader }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/posts`, { headers: authHeader });
      setPosts(res.data);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleSave = async (postData) => {
    if (editingPost?.id) {
      await axios.put(`${API}/admin/posts/${editingPost.id}`, postData, { headers: authHeader });
    } else {
      await axios.post(`${API}/admin/posts`, postData, { headers: authHeader });
    }
    fetchPosts();
    setShowEditor(false);
    setEditingPost(null);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API}/admin/posts/${postId}`, { headers: authHeader });
      showToast('Post deleted');
      fetchPosts();
    } catch {
      showToast('Failed to delete post', 'error');
    }
  };

  if (showEditor) {
    return <PostEditor post={editingPost} onSave={handleSave} onCancel={() => { setShowEditor(false); setEditingPost(null); }} />;
  }

  return (
    <div className="space-y-6" data-testid="posts-manager">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Posts</h1>
        <Button onClick={() => { setEditingPost(null); setShowEditor(true); }} className="bg-primary hover:bg-primary-hover" data-testid="add-new-post-btn">
          <Plus className="w-4 h-4 mr-2" /> Add New Post
        </Button>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : posts.length === 0 ? (
          <div className="p-8 text-center text-gray-500" data-testid="no-posts-msg">No posts yet. Create your first post!</div>
        ) : (
          <table className="w-full" data-testid="posts-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50" data-testid={`post-row-${post.id}`}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{post.title}</div>
                    <div className="text-sm text-gray-500">/{post.slug}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${post.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{new Date(post.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => { setEditingPost(post); setShowEditor(true); }} className="text-blue-600 hover:text-blue-800 mr-3" data-testid={`edit-post-${post.id}`}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(post.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-post-${post.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
